import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { X as XIcon, Search, Package, Phone, Mail, MapPin } from "lucide-react";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const dt = (iso) => (iso ? new Date(iso).toLocaleString("en-IN") : "—");
const statusBadge = (s) => (
  <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${
    s === "delivered" ? "bg-emerald-100 text-emerald-700" :
    s === "cancelled" ? "bg-red-100 text-red-700" :
    s === "shipped" ? "bg-blue-100 text-blue-700" :
    "bg-amber-100 text-amber-700"
  }`}>{(s || "—").replace(/_/g, " ")}</span>
);

export default function AdminCustomers() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null); // customer row selected for history panel

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/customers/summary");
      setRows(data.customers || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const [tab, setTab] = useState("all");
  const [abandoned, setAbandoned] = useState([]);
  const [abLoading, setAbLoading] = useState(false);
  useEffect(() => {
    if (tab !== "abandoned") return;
    setAbLoading(true);
    api.get("/admin/customers/abandoned")
      .then(({ data }) => setAbandoned(data.abandoned || []))
      .catch(() => {})
      .finally(() => setAbLoading(false));
  }, [tab]);

  const abandonedFiltered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return abandoned;
    return abandoned.filter((r) => [r.name, r.email, r.phone, r.order_number].filter(Boolean).join(" ").toLowerCase().includes(s));
  }, [abandoned, q]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) => [r.name, r.email, r.phone, r.city, r.state, r.last_order_number].filter(Boolean).join(" ").toLowerCase().includes(s));
  }, [rows, q]);

  const totalCustomers = rows.length;
  const buyingCustomers = rows.filter((r) => r.orders_count > 0).length;
  const lifetime = rows.reduce((s, r) => s + (r.lifetime_value || 0), 0);

  return (
    <div className="space-y-6" data-testid="admin-customers">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Directory</div>
          <h1 className="font-serif text-3xl md:text-4xl">Customers</h1>
          <p className="text-sm text-muted-foreground mt-1">Registered accounts + every guest who has ordered — with lifetime value and full order history.</p>
        </div>
        <div className="relative w-full md:w-72">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" strokeWidth={1.5} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, phone, email, city, order…"
            className="w-full border hairline rounded-full pl-9 pr-3 py-2 text-sm outline-none focus:border-[#B8860B]"
            data-testid="customer-search"
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="border hairline rounded-xl p-4 bg-white" data-testid="stat-total-customers">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Total</div>
          <div className="font-serif text-2xl mt-1">{totalCustomers}</div>
        </div>
        <div className="border hairline rounded-xl p-4 bg-white" data-testid="stat-buying-customers">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Buyers</div>
          <div className="font-serif text-2xl mt-1">{buyingCustomers}</div>
        </div>
        <div className="border hairline rounded-xl p-4 bg-white" data-testid="stat-lifetime">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">GMV</div>
          <div className="font-serif text-2xl mt-1">{inr(lifetime)}</div>
        </div>
      </div>

      <div className="flex gap-2" data-testid="customer-tabs">
        <button onClick={() => setTab("all")} className={`rounded-full px-4 py-2 text-xs uppercase tracking-widest border ${tab === "all" ? "btn-gold border-transparent" : "hairline bg-white"}`} data-testid="cust-tab-all">All Customers</button>
        <button onClick={() => setTab("abandoned")} className={`rounded-full px-4 py-2 text-xs uppercase tracking-widest border ${tab === "abandoned" ? "btn-gold border-transparent" : "hairline bg-white"}`} data-testid="cust-tab-abandoned">Payment Abandoned</button>
      </div>

      {tab === "all" && (
      <div className="border hairline rounded-xl bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-widest text-muted-foreground">
            <tr>
              <th className="p-3">Customer</th>
              <th className="p-3 hidden md:table-cell">Contact</th>
              <th className="p-3">Orders</th>
              <th className="p-3">Lifetime</th>
              <th className="p-3 hidden md:table-cell">Last Order</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground text-sm">Loading…</td></tr>}
            {!loading && filtered.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground text-sm">No customers yet.</td></tr>}
            {filtered.map((r, i) => (
              <tr key={(r.phone_key || r.user_id || i) + "-" + i} className="border-t hairline align-top hover:bg-muted/30" data-testid={`cust-row-${r.phone_key || r.user_id || i}`}>
                <td className="p-3">
                  <div className="font-medium">{r.name || "—"}</div>
                  <div className="text-[11px] text-muted-foreground truncate max-w-[220px]">{[r.city, r.state].filter(Boolean).join(", ")}</div>
                  {r.is_registered && <span className="mt-1 inline-block text-[9px] uppercase tracking-widest bg-[#B8860B] text-ivory rounded-full px-2 py-0.5">Account</span>}
                </td>
                <td className="p-3 hidden md:table-cell text-xs">
                  {r.phone && <div className="flex items-center gap-1"><Phone className="w-3 h-3 shrink-0" strokeWidth={1.5} />{r.phone}</div>}
                  {r.email && <div className="flex items-center gap-1 text-muted-foreground truncate max-w-[180px]"><Mail className="w-3 h-3 shrink-0" strokeWidth={1.5} />{r.email}</div>}
                </td>
                <td className="p-3 tabular-nums">{r.orders_count || 0}</td>
                <td className="p-3 tabular-nums font-medium">{inr(r.lifetime_value)}</td>
                <td className="p-3 hidden md:table-cell text-xs">
                  <div>{r.last_order_number || "—"}</div>
                  <div className="text-muted-foreground">{r.last_order_at ? dt(r.last_order_at) : ""}</div>
                </td>
                <td className="p-3 text-right">
                  <button
                    onClick={() => setActive(r)}
                    disabled={!(r.orders_count > 0)}
                    className="btn-outline-gold rounded-full px-3 py-1 text-[11px] uppercase tracking-widest disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid={`cust-history-${r.phone_key || r.user_id || i}`}
                  >
                    History
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      {tab === "abandoned" && (
        <div className="space-y-3" data-testid="abandoned-list">
          {abLoading && <div className="border hairline rounded-xl bg-white p-6 text-center text-sm text-muted-foreground">Loading…</div>}
          {!abLoading && abandonedFiltered.length === 0 && (
            <div className="border hairline rounded-xl bg-white p-6 text-center text-sm text-muted-foreground" data-testid="abandoned-empty">No abandoned checkouts yet.</div>
          )}
          {abandonedFiltered.map((r) => (
            <div key={r.phone_key} className="border hairline rounded-xl bg-white p-4" data-testid={`abandoned-${r.phone_key}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div>
                  <div className="font-medium">{r.name || "—"}</div>
                  <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3 mt-0.5">
                    {r.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" strokeWidth={1.5} />{r.phone}</span>}
                    {r.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" strokeWidth={1.5} />{r.email}</span>}
                  </div>
                </div>
                <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full whitespace-nowrap ${r.status === "payment_completed" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`} data-testid={`abandoned-status-${r.phone_key}`}>
                  {r.status === "payment_completed" ? "Payment Completed" : "Payment Abandoned"}
                </span>
              </div>
              {r.address && (r.address.line1 || r.address.city) && (
                <div className="text-xs text-muted-foreground flex items-start gap-1 mb-2">
                  <MapPin className="w-3 h-3 mt-0.5 shrink-0" strokeWidth={1.5} />
                  <span>{[r.address.line1, r.address.line2, r.address.city, r.address.state, r.address.pincode, r.address.country].filter(Boolean).join(", ")}</span>
                </div>
              )}
              <div className="space-y-1 text-xs mb-3">
                {(r.items || []).map((it, i) => (
                  <div key={i} className="flex justify-between gap-2">
                    <span className="truncate">{it.name} × {it.quantity}</span>
                    <span className="tabular-nums shrink-0">{inr(it.line_total ?? (it.price * it.quantity))}</span>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-[11px] border-t hairline pt-2">
                <div><div className="uppercase tracking-widest text-muted-foreground text-[9px]">Cart Total</div><div className="font-medium">{inr(r.cart_total)}</div></div>
                <div><div className="uppercase tracking-widest text-muted-foreground text-[9px]">Checkout At</div><div>{dt(r.checkout_at)}</div></div>
                <div><div className="uppercase tracking-widest text-muted-foreground text-[9px]">Last Activity</div><div>{dt(r.last_activity_at)}</div></div>
                <div><div className="uppercase tracking-widest text-muted-foreground text-[9px]">Payment Attempted</div><div className="font-medium">{r.payment_attempted ? "Yes" : "No"}</div></div>
                <div><div className="uppercase tracking-widest text-muted-foreground text-[9px]">Payment Status</div><div className="font-medium">{r.status === "payment_completed" ? "Paid" : (r.payment_status || "Pending") === "pending" ? "Abandoned" : r.payment_status}</div></div>
                <div><div className="uppercase tracking-widest text-muted-foreground text-[9px]">Last Step</div><div className="font-medium capitalize">{r.last_step || "Payment"}</div></div>
              </div>
              {r.order_number && <div className="mt-2 text-[11px] text-emerald-700 font-medium">Recovered → Order {r.order_number}</div>}
            </div>
          ))}
        </div>
      )}

      {active && <CustomerHistory customer={active} onClose={() => setActive(null)} />}
    </div>
  );
}

function CustomerHistory({ customer, onClose }) {
  const [orders, setOrders] = useState(null);
  const key = customer.phone_key || customer.user_id || customer.email;
  useEffect(() => {
    api.get(`/admin/customers/${encodeURIComponent(key)}/orders`).then(({ data }) => setOrders(data.orders || []));
  }, [key]);
  return (
    <div className="fixed inset-0 z-[80] bg-black/60 flex justify-end" onClick={onClose} data-testid="cust-history-panel">
      <div className="w-full max-w-lg h-full bg-ivory shadow-2xl overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-ivory border-b hairline p-4 flex items-center justify-between z-10">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Customer 360</div>
            <h2 className="font-serif text-xl">{customer.name || "Customer"}</h2>
            <div className="text-xs text-muted-foreground">{[customer.phone, customer.email].filter(Boolean).join(" · ") || "—"}</div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full border hairline flex items-center justify-center" aria-label="Close" data-testid="cust-history-close">
            <XIcon className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="border hairline rounded-lg p-2 bg-white">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Orders</div>
              <div className="font-serif text-lg">{customer.orders_count || 0}</div>
            </div>
            <div className="border hairline rounded-lg p-2 bg-white">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Lifetime</div>
              <div className="font-serif text-lg">{inr(customer.lifetime_value)}</div>
            </div>
            <div className="border hairline rounded-lg p-2 bg-white">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Paid</div>
              <div className="font-serif text-lg">{inr(customer.paid_value)}</div>
            </div>
          </div>

          <div className="text-xs uppercase tracking-widest text-muted-foreground pt-2 flex items-center gap-1">
            <Package className="w-3.5 h-3.5" strokeWidth={1.5} /> Order history
          </div>
          {!orders && <div className="text-sm text-muted-foreground">Loading orders…</div>}
          {orders && orders.length === 0 && <div className="text-sm text-muted-foreground">No orders yet.</div>}
          {orders && orders.map((o) => (
            <div key={o.order_number} className="border hairline rounded-xl bg-white p-3" data-testid={`history-order-${o.order_number}`}>
              <div className="flex items-center justify-between gap-2 mb-1">
                <div className="font-medium text-sm">{o.order_number}</div>
                {statusBadge(o.status)}
              </div>
              <div className="text-[11px] text-muted-foreground">{dt(o.created_at)} · Payment: <span className={`font-medium ${o.payment_status === "paid" ? "text-emerald-700" : "text-amber-700"}`}>{o.payment_status}{o.auto_verified ? " (auto)" : ""}</span></div>
              <div className="mt-2 space-y-1 text-xs">
                {(o.items || []).map((it, i) => (
                  <div key={i} className="flex justify-between gap-2">
                    <span className="truncate">{it.name} × {it.quantity}</span>
                    <span className="tabular-nums shrink-0">{inr(it.line_total)}</span>
                  </div>
                ))}
              </div>
              <div className="mt-2 flex items-center justify-between text-xs">
                <div className="flex items-center gap-1 text-muted-foreground truncate">
                  <MapPin className="w-3 h-3 shrink-0" strokeWidth={1.5} />
                  <span className="truncate">{[o.address?.city, o.address?.state].filter(Boolean).join(", ")}</span>
                </div>
                <div className="font-medium">{inr(o.total)}</div>
              </div>
              {o.utr_reference && <div className="mt-1 text-[10px] font-mono text-muted-foreground">UTR: {o.utr_reference}</div>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
